import { SystemEvent, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { debounce, generateUUID, processAndReplaceQueue } from '@mbrtargeting/metatag-utils';
import { GLOBAL_TARGETING, NATIVE_BACKFILL, RINGIER_GAM_ADSERVER, STROEER_GAM_ADSERVER, STROEER_SPONSORSHIP, STROEER_SSP, YIELDLOVE } from '../../../core/token.js';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { waitForEventPromise } from '../../../loader/essentials/events.js';
import { logger } from '../../../loader/essentials/logger.js';
import { Map2 } from '../../../loader/map2.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ADSLOT_REGISTRY } from '../../../loader/token.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { AdslotAddon, AdslotControllerInterface, LoadIntentionOptions } from './adslot-controller-interface.js';
import { LifecycleAction, LifecycleState } from './adslot-lifecycle-state.js';
import { AdslotQueueState, QueueAction, QueueState } from './adslot-queue-state.js';
import { Adslot, isAdslotInstance } from './adslot.js';
import { CreativeSource } from './creative-source.js';

const log = logger.logGroup({ prefix: 'AdslotController' });

type Queue = {
    queueId: string,
    adslots: Adslot[],
    sources: CreativeSource[],
};

@injectionTarget()
export class AdslotController implements AdslotControllerInterface {

    @inject(ADSLOT_REGISTRY) private adslotRegistry!: AdslotRegistry;
    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;
    @inject(STROEER_SSP) private stroeerSsp!: CreativeSource;
    @inject(STROEER_SPONSORSHIP) private stroeerSponsorship!: CreativeSource;
    @inject(YIELDLOVE) private yieldlove!: CreativeSource;
    @inject(STROEER_GAM_ADSERVER) private stroeerGamAdserver!: CreativeSource;
    @inject(RINGIER_GAM_ADSERVER) private ringierGamAdserver!: CreativeSource;
    @inject(NATIVE_BACKFILL) private nativeBackfill!: CreativeSource;

    /** a lazyly created waterfall chain of creative sources which try to fill the Adslot with an ad */
    #chainPromise: Promise<CreativeSource[]>
    /** a deferred command queue */
    #deferred: Function[] = [];

    private _addons: AdslotAddon[] = [];
    private readonly queueStates: Map2<Adslot, AdslotQueueState>;

    constructor() {
        // a factory function to lazily create a new AdslotQueueState
        const createAdslotQueueState = (adslot: Adslot): AdslotQueueState => {
            const queueState = new AdslotQueueState(adslot);
            queueState.onState(QueueState.QUEUED, async () => {
                if (adslot.lifecycle.inAnyState([LifecycleState.AVAILABLE, LifecycleState.EMPTY, LifecycleState.BLOCKED, LifecycleState.OCCUPIED])) {
                    queueState.dispatch(QueueAction.GETREADY);
                } else {
                    adslot.lifecycle.onState(LifecycleState.AVAILABLE, async () => {
                        queueState.dispatch(QueueAction.GETREADY);
                    }, { once: true });
                }
            });
            queueState.onState(QueueState.READY, async () => {
                log.debug('%s: is now ready', [adslot.slotName.toUpperCase()]);
                this.#checkQueue();
            });
            queueState.onEnter(QueueState.PROCESSING, async () => {
                if (adslot.lifecycle.inAnyState([LifecycleState.EMPTY, LifecycleState.OCCUPIED, LifecycleState.BLOCKED])) {
                    await adslot.lifecycle.dispatch(LifecycleAction.RESET);
                }
            });
            return queueState;
        };
        // a map with Adslot as key and its corresponding AdslotQueueState as value; lazy created if it does not exist yet
        this.queueStates = new Map2<Adslot, AdslotQueueState>(createAdslotQueueState);

        const { stroeerSsp, stroeerSponsorship, yieldlove, ringierGamAdserver, stroeerGamAdserver, nativeBackfill, globalTargeting } = this;
        // a lazyly created waterfall chain of creative sources which try to fill the Adslot with an ad
        this.#chainPromise = Promise
            // run all require
            .all([
                // guess the user location
                //maybeSwitzerland(consentCache.getTcDataPromise()), currently deactivated for testing business logic with ringier
                // get country code - as it is fetched in targeting phase, wait until delivery phase has started
                waitForEventPromise(SystemEvent.DELIVERY_PHASE_STARTED).then(() => globalTargeting.get('cc').first()),
                // wait until core file is loaded and adslots are replaced
                waitForEventPromise(SystemEvent.SDG_CORE_FILE_LOADED).then(() => {
                    // switch AdslotStub to Adslot for all exisitng and future slots
                    this.adslotRegistry.setAdslotClass(Adslot);
                    // helper function to apply all addons to a adslot
                    const applyAddons = (adslot: Adslot): void => this._addons.forEach(addon => addon.registerOnAdslot(adslot));
                    // for all existing Adslots (core impl) add existing all addons
                    this.adslotRegistry.getAdslots().filter(isAdslotInstance).forEach(applyAddons);
                    // for all future Adslot registrations apply all existing addons
                    window.addEventListener(SystemSlotEvent.SDG_SLOT_REGISTERED, ({ detail }) => {
                        const adslot = this.adslotRegistry.getAdslot(detail?.slotName);
                        isAdslotInstance(adslot) && applyAddons(adslot);
                    });
                }),
            ])
            // create the list of creative sources lazyly
            .then(([countryCode]) => [
                stroeerSsp,
                stroeerSponsorship,
                yieldlove,
                ...(countryCode && countryCode === 'ch') ? [ringierGamAdserver] : [],
                stroeerGamAdserver,
                nativeBackfill,
            ])
            // if we are ready to go, run all queued commands
            .finally(() => {
                processAndReplaceQueue(this.#deferred);
            });
    }

    /**
     * Process a queue of adslots.
     *
     * @param queue the queue to process
     */
    #processQueue = ({ queueId, adslots, sources }: Queue): void => {
        // split adslots into two partitions, bare/empty and non-empty/filled
        const adslotsLoadable = adslots.filter(adslot => adslot?.lifecycle?.inAnyState([LifecycleState.AVAILABLE, LifecycleState.EMPTY]));
        const adslotsFilled = adslots.filter(adslot => adslot?.lifecycle?.inAnyState([LifecycleState.OCCUPIED, LifecycleState.BLOCKED]));

        // set state complete to all filled adslots
        for (const adslotFilled of adslotsFilled) {
            this.queueStates.get(adslotFilled).dispatch(QueueAction.COMPLETE);
        }

        // if no adslots are loadable, we are done
        // (no adslots to mark as complete; no adslots to send to a creative source; not sending an empty adslot array to a creative source)
        if (!adslotsLoadable.length) return;

        // get the first and all other creativeSources
        const [currentSource, ...remainingSources] = sources;

        // if no creative source is available, mark all remaining loadable adslots as complete and we are done
        if (!currentSource) {
            for (const adslot of adslotsLoadable) {
                log.info('%s: no creative source can fill the adslot', [adslot.slotName.toUpperCase()]);
                adslot.lifecycle.dispatch(LifecycleAction.CLEAR);
                this.queueStates.get(adslot).dispatch(QueueAction.COMPLETE);
            }
            return;
        }

        // load the adslots with the current creative source
        currentSource.loadSlots({
            adslots: adslotsLoadable,
            done: adslotsDone => this.#processQueue({
                queueId,
                adslots: adslotsDone,
                sources: remainingSources,
            }),
            context: adslot => this.queueStates.get(adslot).context,
        });
    }

    /**
     * When an adslot is READY this function is called.
     * It is debounced which causes the adslots to be grouped and loaded together in one flight for road blocks.
     */
    #checkQueue = debounce(async () => {
        const sources: CreativeSource[] = await this.#chainPromise;
        const queueId: string = generateUUID();
        // get all slots which are currently in state ready
        const readyAdslots: Adslot[] = this.queueStates.filter(([/*adslot*/, queueState]) => queueState.inAnyState([QueueState.READY])).keysArray();
        log.debug('processing waitqueue %s: %o', [queueId, readyAdslots.map(slot => slot.slotName)]);
        // switch all ready slots to processing in order to prevent parallel invocations to adservers
        await Promise.allSettled(readyAdslots.map(adslot => this.queueStates.get(adslot).dispatch(QueueAction.PROCESS)));
        // handle the adslots by the first adserver implementation
        this.#processQueue({
            queueId,
            adslots: readyAdslots,
            sources,
        });
    }, 100);

    /**
     * Mark some adslots to be loaded in the next loading cycle.
     *
     * @param slotNames a list of adslot names
     * @param reloadAds if true, already filled adslots should load again
     */
    public markLoadIntention(slotNames: string[], options: LoadIntentionOptions): void {
        /** when reloadAds === false, filter all AdSlots that are already loaded and dont load them again. */
        const shouldReloadAdslot = (adslot: Adslot) => options?.forceReload || this.queueStates.get(adslot).inAnyState([QueueState.IDLE]);
        // we need to wait until all AdslotStubs are replaced by Adslots before we can continue
        this.#deferred.push(() => {
            for (const slotName of slotNames) {
                // for all given slotNames, get the corresponding Adslot if it exists
                const adslot = this.adslotRegistry.getAdslot(slotName) as Adslot | null;
                // if the Adslot does not exist, log an error and continue with the next slotName
                if (!adslot) {
                    log.error('%s: is not registered. Load command discarded.', [slotName.toUpperCase()]);
                    continue;
                }
                // if the Adslot is already loaded and reloadAds is not true, log an info and continue with the next slotName
                if (!shouldReloadAdslot(adslot)) {
                    log.warn('%s: is already loaded (or currently loading). Load command discarded as this was an unforced reload (reloadAds=false).', [slotName.toUpperCase()]);
                    continue;
                }
                // if the Adslot should load, trigger an state change
                this.queueStates.get(adslot).addContext(options).dispatch(QueueAction.ENQUEUE, {
                    onInvalidTransition: async () => log.warn('%s: is currently not able to (re)load. Load command discarded.', [adslot.slotName.toUpperCase()]),
                });
            }
        });
    }

    /**
     * can be called by AdSlotAddons to be added to all present and future slots.
     * All currently registered slots will be notified and the addon installed
     * @param addon
     */
    public insertAddon(addon: AdslotAddon): void {
        // add addon to the list of existing addons
        this._addons.push(addon);
        // the next line will only return adslots of the addon is added after SystemEvent.SDG_CORE_FILE_LOADED
        // before that event, the addon is only pushed into this._addons and applied when AdslotStub is replaced to Adslot
        this.adslotRegistry.getAdslots().filter(isAdslotInstance).forEach(adslot => {
            addon.registerOnAdslot(adslot);
        });
    }
}
